<script setup lang="ts">
import { X } from 'lucide-vue-next';

const { remove, snackbar } = useSnackbar();

const variantClasses = computed(() => {
  if (snackbar.value === null) return '';

  switch (snackbar.value.variant) {
    case 'info':
      return 'text-blue-500';
    case 'success':
      return 'text-green-500';
    case 'warning':
      return 'text-amber-500';
    case 'error':
      return 'text-red-500';
    default:
      return '';
  }
});

const variantIcon = computed(() => {
  if (snackbar.value === null) return '';

  switch (snackbar.value.variant) {
    case 'info':
      return 'ph:info';
    case 'success':
      return 'ph:check-circle';
    case 'warning':
      return 'ph:warning';
    case 'error':
      return 'ph:warning-circle';
    default:
      return '';
  }
});
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="transform ease-out duration-300 transition"
    leave-active-class="transition ease-in duration-100"
    enter-from-class="-translate-y-4 opacity-0"
    leave-to-class="-translate-y-4 opacity-0"
  >
    <div
      v-if="snackbar !== null"
      class="relative max-w-[calc(100vw-32px)] min-w-xs items-start gap-x-4 overflow-hidden rounded-lg bg-white px-4 py-3 md:max-w-[40rem]"
      shadow="[0_0_20px_rgba(0,0,0,0.15)]"
      :class="[
        snackbar.autoClose && 'pb-4 gap-y-2',
        snackbar.autoClose && 'after:animate-[width_linear] after:animate-duration-[var(--bar-seconds)]',
        snackbar.autoClose && 'after:absolute after:bottom-0 after:content-empty after:h-1',
        snackbar.autoClose && snackbar.variant === 'error' && 'after:bg-red-500/50',
        snackbar.autoClose && snackbar.variant === 'warning' && 'after:bg-amber-500/50',
        snackbar.autoClose && snackbar.variant === 'success' && 'after:bg-green-500/50',
        snackbar.autoClose && snackbar.variant === 'info' && 'after:bg-blue-500/50',
      ]"
      :style="{ '--bar-seconds': `${snackbar.durationMs}ms` }"
      outline="~ 1 neutral-200"
      grid="~ cols-[auto_1fr_auto]"
    >
      <Icon v-if="snackbar.showIcon" :name="variantIcon" size="20" :class="variantClasses" />

      <slot name="title">
        <span
          v-if="snackbar.title"
          class="whitespace-pre-wrap break-all"
          :class="[variantClasses, !snackbar.showIcon && 'col-start-2']"
        >
          {{ snackbar.title }}
        </span>
        <span v-else-if="snackbar.description" class="col-start-2 whitespace-pre-wrap break-all text-sm text-neutral-400 font-normal">
          {{ snackbar.description }}
        </span>
      </slot>

      <SBButton v-if="!snackbar.autoClose" variant="ghost" :lucide-icon="X" @click="() => remove()" />

      <slot name="description">
        <span v-if="snackbar.description && snackbar.title" class="col-start-2 whitespace-pre-wrap break-all text-sm text-neutral-400 font-normal">
          {{ snackbar.description }}
        </span>
      </slot>
    </div>
  </Transition>
</template>

<style>
@keyframes width {
  from {
    width: 100%;
  }
  to {
    width: 0;
  }
}
</style>
